import {I18n} from 'aws-amplify/utils';
import {
    Badge,
    Button,
    Checkbox,
    Group,
    Stack,
    Text,
} from '@mantine/core';

import {
    ALWAYS_GRANTED,
    READ_ONLY_GRANTS,
    ROLE_GRANTS,
    RoleGrantKey,
    RoleGrantSelection,
} from '../services/rolePolicyBuilder';

/**
 * FNP-1400 — the grant checkboxes, extracted so CONNECT and CORRECT share them.
 *
 * ⚠️ ONE LIST, TWO CALLERS, AND THAT IS THE POINT. FNP-1363 established that
 * the tick and the stored declaration are one act: the same checkbox that adds
 * an action to the pasted policy sets the capability Flexi stores, so the two
 * cannot disagree. A tenant correcting a declaration later must meet the SAME
 * questions in the SAME words, or the correction screen becomes a second place
 * to describe permissions and the two drift.
 *
 * This was lifted out of `RoleBuilderPanel` unchanged. That panel keeps
 * everything around it — the policy to paste, the trust policy, the external id
 * — because those belong to CONNECTING, where a role is being created. A
 * correction has no policy to hand over: the role already exists, and the
 * tenant is telling Flexi what they granted it.
 *
 * ⚠️ WHAT THIS COMPONENT DOES NOT KNOW. It has no opinion about whether it is
 * connecting or correcting, and no mutation of its own. It renders a selection
 * and reports changes. Both callers own their own submit.
 */
export const GrantChecklist = (
    {
        selection,
        onChange,
        intro,
        showReadOnlyShortcut = true,
        only,
    }: {
        readonly selection: RoleGrantSelection,
        readonly onChange: (selection: RoleGrantSelection) => void,
        /** The sentence above the list. Differs between connect and correct. */
        readonly intro: string,
        /**
         * ⚠️ OFFERED ON CONNECT, WITHHELD ON CORRECT. "Start read-only" is the
         * reassuring first step for someone deciding what to grant. On a
         * correction it would be a one-click revocation of everything the
         * tenant currently has, sitting under a heading about editing — which
         * is a destructive action wearing a helpful label.
         */
        readonly showReadOnlyShortcut?: boolean,
        /**
         * ⚠️ NARROWS WHICH QUESTIONS ARE ASKED, and a caller that narrows it
         * must also narrow what it SENDS. The correction drawer shows only the
         * DNS grants because the app cannot read the certificate declaration
         * back yet (FNP-1398); showing ticks whose state is invented, and whose
         * submission would revoke real grants, is worse than not asking.
         */
        readonly only?: readonly RoleGrantKey[],
    },
) => {

    const toggle = (key: RoleGrantKey) => {
        // Read is the minimum to validate at all, so it is rendered as a fixed
        // fact rather than a control that appears to do something.
        if (key === ALWAYS_GRANTED) {
            return;
        }
        onChange({...selection, [key]: !selection[key]});
    };

    return (
        <Stack gap={'sm'}>
            <Text size={'sm'} c={'dimmed'}>
                {I18n.get(intro)}
            </Text>

            {ROLE_GRANTS
                .filter(grant => !only || only.indexOf(grant.key) !== -1)
                .map(grant => {
                const ticked = grant.key === ALWAYS_GRANTED || selection[grant.key];
                return (
                    <Checkbox
                        key={grant.key}
                        data-testid={`grant-${grant.key}`}
                        checked={ticked}
                        // Read is always granted and saying so plainly is
                        // better than a control that silently refuses.
                        disabled={grant.key === ALWAYS_GRANTED}
                        onChange={() => toggle(grant.key)}
                        label={
                            <Group gap={'xs'}>
                                <Text size={'sm'}>{I18n.get(grant.label)}</Text>
                                {/* Destructive grants are the ones customers
                                    most reasonably refuse. Marking them makes
                                    the weight of the decision visible rather
                                    than leaving it in the wording. */}
                                {grant.destructive && (
                                    <Badge size={'xs'} color={'orange'} variant={'light'}>
                                        {I18n.get('destructive')}
                                    </Badge>
                                )}
                            </Group>
                        }
                        description={
                            <Stack gap={2}>
                                <Text size={'xs'} c={'dimmed'}>
                                    {I18n.get(grant.description)}
                                </Text>
                                {/* ⚠️ WHAT WITHHOLDING COSTS, SHOWN ONLY WHEN
                                    THEY HAVE WITHHELD IT. Before the decision
                                    it is noise; after it, it is the answer to
                                    "what did I just give up?" — asked and
                                    answered here rather than discovered at a
                                    failed action months later. */}
                                {!ticked && grant.withholdingCost && (
                                    <Text
                                        size={'xs'}
                                        c={'dimmed'}
                                        fs={'italic'}
                                        data-testid={`withholding-${grant.key}`}
                                    >
                                        {I18n.get(grant.withholdingCost)}
                                    </Text>
                                )}
                            </Stack>
                        }
                    />
                );
            })}

            {/* ⚠️ READ-ONLY IN ONE CLICK, NOT FOUR UNTICKS. The published
                document already tells customers they may grant less; this
                is what makes that a real path rather than a footnote, and
                it is the choice a hesitant customer most wants. */}
            {showReadOnlyShortcut && (
                <Group>
                    <Button
                        variant={'subtle'}
                        size={'compact-sm'}
                        data-testid={'grant-read-only'}
                        onClick={() => onChange(READ_ONLY_GRANTS)}
                    >
                        {I18n.get('Start read-only')}
                    </Button>
                </Group>
            )}
        </Stack>
    );
};
